import React, { useMemo, useCallback, useState, useEffect } from "react"
import { Formik, Form as FormikForm } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { Label, Button, ButtonGroup, FormFeedback, Row, Col, Card, CardBody } from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VehicleIdSelect from "components/Common/VehicleIdSelect"

import { useQueryClient } from "@tanstack/react-query"

import { useCreateAccessoriesUsage, useEditAccessoriesUsage } from "hooks/useAccessoriesUsage"
import { useAccessoriesListsWithStock, ACCESSORIES_LIST_QUERY_KEY } from "hooks/useAccessoriesLists"
import { useCustomers } from "hooks/useCustomer"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { calculateSaleSubtotal } from "utils/accessoryUtils"

const emptyItem = { accessory_id: "", unit_id: "", quantity: "", sale_price: "" }

const AccessoriesUsageAddEdit = ({ modal, toggleModal, isEdit, editedData, preSelectedVehicle, preSelectedCustomer, hideVehicleSelect = false, moneyReceiptId, onSuccess }) => {
  // -------------------- Fetch dropdown options --------------------
  const { raw: rawOptions, isLoading: optionLoading } = useFilteredVehicleOptions()

  // -------------------- State for created options --------------------
  const [createdAccessories, setCreatedAccessories] = useState([])
  const [createdCustomers, setCreatedCustomers] = useState([])
  const [createdUnits, setCreatedUnits] = useState([])

  useEffect(() => {
    if (!modal) {
      setCreatedAccessories([])
      setCreatedCustomers([])
      setCreatedUnits([])
    }
  }, [modal])

  // -------------------- Fetch accessories with stock info --------------------
  const { data: accessoriesWithStockData } = useAccessoriesListsWithStock({ all: true })

  const accessoriesWithStock = useMemo(() => {
    return accessoriesWithStockData?.data ?? []
  }, [accessoriesWithStockData])

  // -------------------- Fetch customers --------------------
  const { data: customersData, isLoading: customerLoading } = useCustomers({ all: true })

  const customerOptions = useMemo(() => {
    const list = Array.isArray(customersData?.data) ? customersData.data : []
    const dbCustomers = list.map(c => ({
      value: c.id?.toString(),
      label: c.mobile ? `${c.name} — ${c.mobile}` : c.name,
    }))
    // ensure the edited usage's customer stays selectable
    if (
      editedData?.customer?.id &&
      !dbCustomers.some(o => o.value === editedData.customer.id?.toString())
    ) {
      dbCustomers.unshift({
        value: editedData.customer.id?.toString(),
        label: editedData.customer.name,
      })
    }
    // ensure the money receipt's customer stays selectable
    if (
      preSelectedCustomer?.id &&
      !dbCustomers.some(o => o.value === preSelectedCustomer.id?.toString())
    ) {
      dbCustomers.unshift({
        value: preSelectedCustomer.id?.toString(),
        label: preSelectedCustomer.mobile
          ? `${preSelectedCustomer.name} — ${preSelectedCustomer.mobile}`
          : preSelectedCustomer.name,
      })
    }
    const created = createdCustomers.map(c => ({ value: c.id, label: c.name }))
    return [...dbCustomers, ...created]
  }, [customersData, createdCustomers, editedData, preSelectedCustomer])

  // -------------------- Combined options --------------------
  const employeeOptions = useMemo(
    () => (rawOptions?.employees ?? []).map(e => ({ value: e.id?.toString(), label: e.name })),
    [rawOptions?.employees]
  )

  const accessoryOptions = useMemo(() => {
    const dbAccessories = accessoriesWithStock.map(a => ({
      value: a.id?.toString(),
      label: a.name,
      available_stock: a.available_stock ?? 0,
      unit_name: a.unit?.name || "",
      sale_price: a.sale_price ?? 0,
    }))
    const createdAccessoryOptions = createdAccessories.map(a => ({
      value: a.id,
      label: a.name,
      available_stock: null,
      unit_name: "",
      sale_price: 0,
    }))
    return [...dbAccessories, ...createdAccessoryOptions]
  }, [accessoriesWithStock, createdAccessories])

  // Set of real DB accessory ids — used to tell a saved accessory from an inline-created one.
  const dbAccessoryIds = useMemo(
    () => new Set(accessoriesWithStock.map(a => String(a.id))),
    [accessoriesWithStock]
  )

  const isNewAccessory = useCallback(
    (accessoryId) => !!accessoryId && !dbAccessoryIds.has(String(accessoryId)),
    [dbAccessoryIds]
  )

  // -------------------- Unit options (for inline-created accessories) --------------------
  const unitOptions = useMemo(() => {
    const dbUnits = (rawOptions?.units ?? []).map(u => ({ value: u.id?.toString(), label: u.name }))
    const created = createdUnits.map(u => ({ value: u.id, label: u.name }))
    return [...dbUnits, ...created]
  }, [rawOptions?.units, createdUnits])

  // Helper to get available stock for an accessory
  const getAvailableStock = useCallback((accessoryId) => {
    const option = accessoryOptions.find(o => String(o.value) === String(accessoryId))
    return option?.available_stock ?? null
  }, [accessoryOptions])

  const queryClient = useQueryClient()

  const { mutateAsync: createMutation, isPending: createLoading } = useCreateAccessoriesUsage()
  const { mutateAsync: editMutation, isPending: editLoading } = useEditAccessoriesUsage()

  // -------------------- Form Initial Values --------------------
  const initialValues = useMemo(() => {
    const initialItems = editedData?.items?.length
      ? editedData.items.map(item => ({
          accessory_id: item.accessory_id?.toString() || "",
          unit_id: "",
          quantity: item.quantity || "",
          sale_price: item.sale_price || "",
        }))
      : [{ ...emptyItem }]

    let vehicleValue = ""
    if (editedData?.vehicle?.id) {
      vehicleValue = {
        label: editedData.vehicle.stock_id + " - " + (editedData.vehicle.full_chassis || ""),
        value: editedData.vehicle.id,
      }
    } else if (preSelectedVehicle && !isEdit) {
      vehicleValue = preSelectedVehicle.value
        ? preSelectedVehicle
        : { label: preSelectedVehicle.stock_id + " - " + (preSelectedVehicle.full_chassis || ""), value: preSelectedVehicle.id }
    }

    return {
      usage_date: editedData?.usage_date || "",
      usage_by_id: editedData?.usage_by_id?.toString() || "",
      customer_id:
        editedData?.customer_id?.toString() ||
        (!isEdit && preSelectedCustomer?.id ? preSelectedCustomer.id.toString() : ""),
      vehicle_id: vehicleValue,
      bill_type: editedData?.bill_type || "package",
      package_price: editedData?.package_price ?? "",
      discount: editedData?.discount ?? "",
      items: initialItems,
    }
  }, [editedData, preSelectedVehicle, preSelectedCustomer, isEdit])

  // -------------------- Validation Schema --------------------
  const validationSchema = useMemo(() => {
    const baseSchema = {
      usage_date: Yup.date().required("Please select usage date"),
      usage_by_id: Yup.string().required("Please select an employee"),
      bill_type: Yup.string().oneOf(["package", "individual"]).required("Select a bill type"),
      package_price: Yup.number().when("bill_type", {
        is: "package",
        then: schema => schema.required("Enter package price").min(0, "Package price must be 0 or more"),
        otherwise: schema => schema.min(0),
      }),
      discount: Yup.number().min(0, "Discount must be 0 or more"),
      items: Yup.array()
        .of(
          Yup.object({
            accessory_id: Yup.string().required("Select an accessory"),
            unit_id: Yup.string().when("accessory_id", (accessory_id, schema) => {
              const idStr = String(accessory_id ?? "")
              if (!idStr) {
                return schema
              }
              return dbAccessoryIds.has(idStr) ? schema : schema.required("Select a unit")
            }),
            quantity: Yup.number()
              .required("Enter quantity")
              .test(
                "max-stock",
                "Quantity exceeds available stock",
                (value, context) => {
                  const available = getAvailableStock(context.parent?.accessory_id)
                  if (available === null || available === undefined) return true
                  return Number(value) <= available
                },
              )
              .moreThan(0, "Quantity must be greater than 0"),
            sale_price: Yup.number().min(0, "Sale price must be 0 or more"),
          })
        )
        .min(1, "Add at least one accessory"),
    }

    if (!hideVehicleSelect) {
      baseSchema.vehicle_id = Yup.mixed().required("Please select a vehicle")
    }

    return Yup.object(baseSchema)
  }, [hideVehicleSelect, getAvailableStock, dbAccessoryIds])

  // -------------------- Handle Option Creation --------------------
  const handleCreateOption = useCallback((inputValue) => {
    const tempId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setCreatedAccessories(prev => [...prev, { id: tempId, name: inputValue }])
  }, [])

  const handleCreateCustomer = useCallback((inputValue) => {
    const tempId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setCreatedCustomers(prev => [...prev, { id: tempId, name: inputValue }])
  }, [])

  const handleCreateUnit = useCallback((inputValue) => {
    const tempId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setCreatedUnits(prev => [...prev, { id: tempId, name: inputValue }])
  }, [])

  // -------------------- Submit Handler --------------------
  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        // Newly created options carry a temp `new_*` id — send the typed name so the backend creates the record.
        const resolveCreated = (id, createdList) => {
          if (id && String(id).startsWith("new_")) {
            return createdList.find(c => c.id === id)?.name ?? id
          }
          return id
        }

        const payload = {
          ...values,
          vehicle_id: values.vehicle_id?.value || values.vehicle_id,
          money_receipt_id: moneyReceiptId ?? editedData?.money_receipt_id ?? null,
          customer_id: resolveCreated(values.customer_id, createdCustomers) || null,
          bill_type: values.bill_type,
          package_price: Number(values.package_price) || 0,
          discount: Number(values.discount) || 0,
          items: values.items.map(item => ({
            ...item,
            quantity: Number(item.quantity),
            sale_price: Number(item.sale_price) || 0,
            accessory_id: resolveCreated(item.accessory_id, createdAccessories),
            // Only inline-created accessories need a unit; existing ones already carry their own.
            unit_id: resolveCreated(item.unit_id, createdUnits) || null,
          })),
        }

        const action = isEdit ? editMutation({ id: editedData.id, ...payload }) : createMutation(payload)
        const res = await action

        // Inline-created accessories are persisted by the backend — refetch the accessory dropdown source.
        queryClient.invalidateQueries({ queryKey: [[ACCESSORIES_LIST_QUERY_KEY, "with-stock"]] })

        toast.success(res?.message ?? `Accessories Usage ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        toggleModal()
        onSuccess?.()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save accessories usage.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, onSuccess, moneyReceiptId, createdCustomers, createdAccessories, createdUnits, queryClient]
  )

  const buttonText = isEdit ? "Save Changes" : "Add Usage"
  const isSubmitDisabled = editLoading || createLoading

  // -------------------- Render --------------------
  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Accessories Usage" : "Add Accessories Usage"}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize={isEdit}>
        {formik => {
          const addItem = () => formik.setFieldValue("items", [...formik.values.items, { ...emptyItem }])
          const removeItem = index => {
            const newItems = formik.values.items.filter((_, i) => i !== index)
            formik.setFieldValue("items", newItems.length ? newItems : [{ ...emptyItem }])
          }

          const isPackage = formik.values.bill_type === "package"
          const subtotal = isPackage
            ? Number(formik.values.package_price) || 0
            : calculateSaleSubtotal(formik.values.items)
          const discount = Number(formik.values.discount) || 0
          const grandTotal = Math.max(subtotal - discount, 0)

          return (
            <FormikForm>
              {/* Usage Date */}
              <div className="mb-3">
                <Label htmlFor="usage_date">Usage Date <span className="text-danger">*</span></Label>
                <InputField
                  type="datetime-local"
                  name="usage_date"
                  id="usage_date"
                  value={formik.values.usage_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.usage_date && !!formik.errors.usage_date}
                />
                {formik.touched.usage_date && formik.errors.usage_date && <FormFeedback type="invalid">{formik.errors.usage_date}</FormFeedback>}
              </div>

              {/* Employee */}
              <FormikCreatableSelect
                name="usage_by_id"
                label="Used By"
                formik={formik}
                options={employeeOptions}
                placeholder="Select employee"
                loading={optionLoading}
                required
              />

              {/* Customer */}
              {preSelectedCustomer ? (
                <div className="mb-3">
                  <Label>Customer</Label>
                  <InputField
                    type="text"
                    value={
                      preSelectedCustomer.mobile
                        ? `${preSelectedCustomer.name} — ${preSelectedCustomer.mobile}`
                        : preSelectedCustomer.name || ""
                    }
                    disabled
                  />
                </div>
              ) : (
                <FormikCreatableSelect
                  name="customer_id"
                  label="Customer"
                  formik={formik}
                  options={customerOptions}
                  placeholder="Select customer or type a name to add"
                  loading={customerLoading}
                  onCreateOption={handleCreateCustomer}
                />
              )}

              {/* Vehicle */}
              {hideVehicleSelect ? (
                <div className="mb-3">
                  <Label>Vehicle ID</Label>
                  <InputField
                    type="text"
                    value={preSelectedVehicle ? (preSelectedVehicle.label || preSelectedVehicle.stock_id + " - " + (preSelectedVehicle.full_chassis || "")) : formik.values.vehicle_id?.label || ""}
                    disabled
                  />
                </div>
              ) : (
                <VehicleIdSelect formik={formik} />
              )}

              {/* Bill Type */}
              <div className="mb-3">
                <Label className="d-block">Bill Type <span className="text-danger">*</span></Label>
                <ButtonGroup size="sm">
                  <Button
                    type="button"
                    color={formik.values.bill_type === "individual" ? "primary" : "light"}
                    outline={formik.values.bill_type !== "individual"}
                    onClick={() => formik.setFieldValue("bill_type", "individual")}
                  >
                    Individual Items
                  </Button>
                  <Button
                    type="button"
                    color={formik.values.bill_type === "package" ? "primary" : "light"}
                    outline={formik.values.bill_type !== "package"}
                    onClick={() => formik.setFieldValue("bill_type", "package")}
                  >
                    Package
                  </Button>
                </ButtonGroup>
              </div>

              {/* Accessories Items */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0 fw-semibold">
                    Accessories <span className="text-danger">*</span>
                    <span className="badge bg-light text-muted ms-2">{formik.values.items.length}</span>
                  </Label>
                  <Button color="primary" size="sm" outline type="button" onClick={addItem}>
                    <i className="mdi mdi-plus me-1" />Add Accessory
                  </Button>
                </div>

                {typeof formik.errors.items === "string" && (
                  <div className="text-danger small mb-2">{formik.errors.items}</div>
                )}

                <div className="border rounded">
                  {formik.values.items.map((item, index) => {
                    const availableStock = getAvailableStock(item.accessory_id)
                    const selectedOption = accessoryOptions.find(o => String(o.value) === String(item.accessory_id))
                    const stockClass = availableStock === null
                      ? "bg-secondary-subtle text-secondary"
                      : availableStock > 0
                        ? "bg-success-subtle text-success"
                        : "bg-danger-subtle text-danger"
                    const accessoryIsNew = isNewAccessory(item.accessory_id)

                    return (
                      <div key={index} className={`p-3 ${index > 0 ? "border-top" : ""}`}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary-subtle text-primary">#{index + 1}</span>
                            {item.accessory_id && selectedOption && (
                              <span className={`badge ${stockClass}`}>
                                Stock: {availableStock === null
                                  ? "N/A (new)"
                                  : `${availableStock} ${selectedOption.unit_name}`.trim()}
                              </span>
                            )}
                          </div>
                          <Button
                            color="link"
                            size="sm"
                            type="button"
                            className="text-danger p-0 lh-1"
                            onClick={() => removeItem(index)}
                            title="Remove accessory"
                          >
                            <i className="mdi mdi-delete-outline fs-5" />
                          </Button>
                        </div>

                        <Row className="g-2">
                          <Col md={6}>
                            <FormikCreatableSelect
                              name={`items.${index}.accessory_id`}
                              label="Accessory"
                              formik={formik}
                              options={accessoryOptions}
                              placeholder="Select accessory"
                              loading={optionLoading}
                              required
                              onCreateOption={handleCreateOption}
                              onChange={(value, selected) => {
                                formik.setFieldValue(`items.${index}.sale_price`, selected?.sale_price ?? 0)
                                // Reset unit: existing accessory keeps its own, new one needs a fresh pick.
                                formik.setFieldValue(`items.${index}.unit_id`, "")
                              }}
                            />
                          </Col>

                          {item.accessory_id && (
                            <Col md={6}>
                              {accessoryIsNew ? (
                                <FormikCreatableSelect
                                  name={`items.${index}.unit_id`}
                                  label="Unit"
                                  formik={formik}
                                  options={unitOptions}
                                  placeholder="Select or type a unit"
                                  loading={optionLoading}
                                  required
                                  onCreateOption={handleCreateUnit}
                                />
                              ) : (
                                <>
                                  <Label>Unit</Label>
                                  <InputField
                                    type="text"
                                    value={selectedOption?.unit_name || "—"}
                                    disabled
                                  />
                                </>
                              )}
                            </Col>
                          )}

                          <Col md={6}>
                            <Label>Quantity <span className="text-danger">*</span></Label>
                            <InputField
                              type="number"
                              name={`items.${index}.quantity`}
                              value={item.quantity}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={formik.touched.items?.[index]?.quantity && !!formik.errors.items?.[index]?.quantity}
                            />
                            {formik.touched.items?.[index]?.quantity && formik.errors.items?.[index]?.quantity && (
                              <FormFeedback type="invalid" className="d-block">
                                {formik.errors.items[index].quantity === "Quantity must be greater than 0"
                                  ? "Quantity must be greater than 0"
                                  : `Available stock is ${availableStock}`}
                              </FormFeedback>
                            )}
                          </Col>
                          {!isPackage && (
                            <Col md={6}>
                              <Label>Sale Price</Label>
                              <InputField
                                type="number"
                                name={`items.${index}.sale_price`}
                                value={item.sale_price}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                step="0.01"
                                min="0"
                                invalid={formik.touched.items?.[index]?.sale_price && !!formik.errors.items?.[index]?.sale_price}
                              />
                              {formik.touched.items?.[index]?.sale_price && formik.errors.items?.[index]?.sale_price && (
                                <FormFeedback type="invalid" className="d-block">
                                  {formik.errors.items[index].sale_price}
                                </FormFeedback>
                              )}
                            </Col>
                          )}
                        </Row>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Package Price */}
              {isPackage && (
                <div className="mb-3">
                  <Label>Package Price <span className="text-danger">*</span></Label>
                  <InputField
                    type="number"
                    name="package_price"
                    value={formik.values.package_price}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    step="0.01"
                    min="0"
                    invalid={formik.touched.package_price && !!formik.errors.package_price}
                  />
                  {formik.touched.package_price && formik.errors.package_price && (
                    <FormFeedback type="invalid" className="d-block">{formik.errors.package_price}</FormFeedback>
                  )}
                </div>
              )}

              {/* Discount */}
              <div className="mb-3">
                <Label>Discount</Label>
                <InputField
                  type="number"
                  name="discount"
                  value={formik.values.discount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  step="0.01"
                  min="0"
                  invalid={formik.touched.discount && !!formik.errors.discount}
                />
                {formik.touched.discount && formik.errors.discount && (
                  <FormFeedback type="invalid" className="d-block">{formik.errors.discount}</FormFeedback>
                )}
              </div>

              {/* Total preview */}
              <Card className="mb-3 border">
                <CardBody className="py-2">
                  <div className="d-flex justify-content-between"><span>Sub Total</span><span>{subtotal.toLocaleString()}</span></div>
                  <div className="d-flex justify-content-between"><span>Discount</span><span>- {discount.toLocaleString()}</span></div>
                  <div className="d-flex justify-content-between fw-bold border-top pt-1 mt-1"><span>Total</span><span>{grandTotal.toLocaleString()}</span></div>
                </CardBody>
              </Card>

              {/* Submit */}
              <div className="text-end mt-3">
                <Button color="success" type="submit" disabled={isSubmitDisabled}>{buttonText}</Button>
              </div>
            </FormikForm>
          )
        }}
      </Formik>
    </ThemeModal>
  )
}

export default AccessoriesUsageAddEdit
